import { Icon } from "@iconify/react";
import type { MouseEvent, ReactNode } from "react";

interface ButtonProps {
  text: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  isLoading?: boolean;
  type?: "button" | "submit" | "reset";
  icon?: boolean;
  iconName?: string;
  iconNode?: ReactNode;
  iconPosition?: "start" | "end";
  disabled?: boolean;
  fontSize?: number;
  variant?: "default" | "bare";
}

export function Button({
  text,
  onClick,
  className = "",
  isLoading = false,
  type = "submit",
  icon = false,
  iconName,
  iconNode,
  iconPosition = "end",
  disabled = false,
  fontSize = 24,
  variant = "default",
}: Readonly<ButtonProps>) {
  const defaultClasses =
    "relative px-8 py-4 rounded-[50px] text-sm font-normal leading-[23px] " +
    "h-12 flex justify-center items-center transition-all cursor-pointer " +
    "outline-none duration-300";

  const bareClasses =
    "relative inline-flex items-center justify-center gap-2 transition-all cursor-pointer outline-none duration-300";

  const disabledClasses =
    "bg-[#444CE799] text-gray-50 hover:bg-[#444CE799] hover:cursor-not-allowed";

  const baseClasses = variant === "bare" ? bareClasses : defaultClasses;

  const finalButtonClass = `${baseClasses} ${className} ${
    disabled && variant === "default" ? disabledClasses : ""
  }`;

  const showIconify = icon || !!iconName;
  const iconToShow = iconName || "hugeicons:arrow-left-02";

  const renderIconify = (spinning = false) =>
    showIconify ? (
      <Icon
        icon={spinning ? (iconName as string) : iconToShow}
        fontSize={fontSize}
        className={`shrink-0 ${spinning ? "animate-spin" : ""}`}
      />
    ) : null;

  const startIcons = (
    <>
      {renderIconify(isLoading)}
      {iconNode}
    </>
  );

  const endIcons = (
    <>
      {renderIconify(isLoading)}
      {iconNode}
    </>
  );

  if (variant === "bare") {
    return (
      <button
        className={finalButtonClass}
        onClick={onClick}
        type={type}
        disabled={isLoading || disabled}
      >
        {iconPosition === "start" && startIcons}
        <span>{text}</span>
        {iconPosition === "end" && endIcons}
      </button>
    );
  }

  const defaultContent = (
    <>
      {iconPosition === "start" && (
        <>
          {renderIconify()}
          {iconNode}
        </>
      )}
      <span>{text}</span>
      {iconPosition === "end" && (
        <>
          {renderIconify()}
          {iconNode}
        </>
      )}
    </>
  );

  return (
    <button
      className={finalButtonClass}
      onClick={onClick}
      type={type}
      disabled={isLoading || disabled}
    >
      <span className="relative flex items-center justify-center gap-2 shrink-0 w-full">
        <span className="invisible flex items-center justify-center gap-2">
          {defaultContent}
        </span>

        <span
          className={`absolute inset-0 flex items-center justify-center gap-2 transition-opacity duration-300 ${
            isLoading ? "opacity-0" : "opacity-100"
          }`}
        >
          {defaultContent}
        </span>

        <span
          className={`absolute inset-0 flex items-center justify-center gap-2 transition-opacity duration-100 ${
            isLoading ? "opacity-100" : "opacity-0"
          }`}
        >
          {renderIconify(true)}
          {iconNode}
          <span>{text}</span>
        </span>
      </span>
    </button>
  );
}
